// Dimensions control component with width/height inputs and aspect lock
import React from 'react';
import { Box, Flex, VStack, HStack, Text, Input, IconButton } from '@chakra-ui/react';
import { Settings, Link, Unlink } from 'lucide-react';
import { ImageItem } from '../types';

interface DimensionsControlProps {
  width: number;
  height: number;
  aspectLocked: boolean;
  currentImage: ImageItem | undefined;
  onWidthChange: (width: number) => void;
  onHeightChange: (height: number) => void;
  onToggleAspectLock: () => void;
}

export const DimensionsControl: React.FC<DimensionsControlProps> = ({
  width,
  height,
  aspectLocked,
  currentImage,
  onWidthChange,
  onHeightChange,
  onToggleAspectLock,
}) => {
  return (
    <Box
      bg="background.secondary"
      borderRadius="xl"
      p={5}
      border="1px solid"
      borderColor="border.default"
    >
      <HStack mb={4}>
        <Settings size={14} color="#A71C29" />
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color="text.secondary"
          textTransform="uppercase"
          letterSpacing="wider"
          fontFamily="secondary"
        >
          Dimensions
        </Text>
      </HStack>

      <Flex gap={3} align="center">
        <VStack flex={1} align="stretch" spacing={1}>
          <Text fontSize="xs" color="text.secondary" fontFamily="secondary">
            Width
          </Text>
          <Input
            type="number"
            value={width}
            onChange={(e) => onWidthChange(parseInt(e.target.value) || 0)}
            bg="background.white"
            border="1px solid"
            borderColor="border.default"
            color="text.primary"
            _focus={{
              borderColor: 'brand.primary.DEFAULT',
              boxShadow: '0 0 0 1px #A71C29',
            }}
            fontFamily="secondary"
          />
        </VStack>

        <IconButton
          aria-label="Toggle aspect ratio lock"
          icon={aspectLocked ? <Link size={14} /> : <Unlink size={14} />}
          bg={aspectLocked ? 'brand.primary.DEFAULT' : 'background.tertiary'}
          color={aspectLocked ? 'white' : 'text.secondary'}
          _hover={{ opacity: 0.8 }}
          onClick={onToggleAspectLock}
          mt={5}
          size="sm"
        />

        <VStack flex={1} align="stretch" spacing={1}>
          <Text fontSize="xs" color="text.secondary" fontFamily="secondary">
            Height
          </Text>
          <Input
            type="number"
            value={height}
            onChange={(e) => onHeightChange(parseInt(e.target.value) || 0)}
            bg="background.white"
            border="1px solid"
            borderColor="border.default"
            color="text.primary"
            _focus={{
              borderColor: 'brand.primary.DEFAULT',
              boxShadow: '0 0 0 1px #A71C29',
            }}
            fontFamily="secondary"
          />
        </VStack>
      </Flex>

      <Text fontSize="xs" color="text.secondary" mt={3} fontFamily="secondary">
        Original: {currentImage?.originalWidth} × {currentImage?.originalHeight}px
      </Text>
    </Box>
  );
};
